import { CommonModule } from '@angular/common';
import { ApplicationRef, Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { LoginModalComponent } from '@kimbocare/auth';
import {
  authFeature,
  CustomNavigationService,
  extractQueryParameters,
  InvitationResponse,
  IUser,
  LoadingService,
  ModalService,
  NotificationService,
  SessionService,
  UseCaseGroupService,
} from '@kimbocare/core';
import { GlobalSpinnerComponent, KUIModule } from '@kimbocare/ui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, first } from 'rxjs';
import { Store } from '@ngrx/store';

@Component({
  selector: 'app-root',
  imports: [
    CommonModule,
    RouterOutlet,
    KUIModule,
    TranslateModule,
    MatButtonModule,
    GlobalSpinnerComponent,
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  isGuest = false;
  isLoggedIn = false;
  user: IUser | null = null;

  constructor(
    private sessionService: SessionService,
    private appRef: ApplicationRef,
    private router: Router,
    private navigationService: CustomNavigationService,
    private useCaseGroup: UseCaseGroupService,
    private loadingService: LoadingService,
    private modalService: ModalService,
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {
    translateService.addLangs(['en', 'fr']);
    translateService.setDefaultLang('en');
    const previousChoseLanguage = this.sessionService.getLanguage();
    const browserLang = previousChoseLanguage
      ? previousChoseLanguage
      : translateService.getBrowserLang();
    if (browserLang)
      translateService.use(browserLang.match(/en|fr/) ? browserLang : 'en');

    this.sessionService
      .isGuest()
      .subscribe((isGuest) => (this.isGuest = isGuest));

    this.sessionService.isLoggedIn().subscribe((isLoggedIn) => {
      this.isLoggedIn = isLoggedIn;
    });

    this.user = this.sessionService.getAuthState().user;
  }

  ngOnInit(): void {
    //Because query external parameters from angular app when app is starting  if too busy 😅
    const queryParams = extractQueryParameters(window.location.href, [
      'mode',
      'key',
      'identification',
      'invitation_key',
    ]);

    combineLatest([
      this.appRef.isStable.pipe(first((isStable) => isStable)),
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
        first(),
      ),
    ]).subscribe(() => {
      if (queryParams) {
        if (!this.isLoggedIn || this.isGuest) {
          if (
            queryParams['mode'] ||
            queryParams['key'] ||
            queryParams['invitation_key']
          ) {
            this.navigationService.goToWithParams('login', queryParams);
          }
        } else if (this.isLoggedIn && !this.isGuest) {
          if (queryParams['invitation_key']) {
            this.addPendindInvitation(queryParams['invitation_key']);
          } else {
            this.inviteMember(queryParams);
          }
        }
      }
      console.log('App fully loaded and stable!');
    });
  }

  inviteMember(queryParams: any): void {
    this.loadingService.show();
    this.useCaseGroup.verifyAndAddMember(queryParams['key']).subscribe({
      next: (res: InvitationResponse) => {
        setTimeout(() => {
          if (res.status === 400) {
            this.notificationService.warning(
              this.translateService.instant(
                'common.wrong-invitation-account-message',
                {
                  identifier: queryParams['identification'],
                },
              ),
            );
            this.modalService
              .openModal(LoginModalComponent, {
                width: '950px',
                height: 'auto',
                data: {
                  caller: '_SIGN_IN',
                  invitation: {
                    key: queryParams['key'],
                    mode: queryParams['mode'],
                    identifier: queryParams['identification'],
                  },
                },
              })
              .subscribe();
          } else if (res.status === 200) {
            this.sessionService.storeCurrentGroup(res.group);
            this.notificationService.success(
              this.translateService.instant('common.invitation-success'),
            );
          } else if (res.status === 404) {
            this.notificationService.warning(
              this.translateService.instant(
                'common.invitation-not-exist-label',
              ),
            );
          }
        }, 1000);
      },
      error: () => {
        this.notificationService.danger(
          this.translateService.instant('errors.generic'),
        );
        this.loadingService.hide();
      },
      complete: () => {
        this.loadingService.hide();
      },
    });
  }

  addPendindInvitation(uuid: string): void {
    this.loadingService.show();
    this.useCaseGroup.addMember(uuid).subscribe({
      next: () => {
        this.notificationService.success(
          this.translateService.instant('common.add-pending-member'),
        );
      },
      error: (err) => {
        this.notificationService.danger(
          this.translateService.instant(err.translate ?? 'errors.generic'),
        );
        this.loadingService.hide();
      },
      complete: () => {
        this.loadingService.hide();
      },
    });
  }
}
